import React from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import {
  BaseText,
  EmptyResultView,
  Loader,
  Separator,
  SettingsLayout,
  SubscriptionPlanItem,
  SubscriptionPlanItemStyleEnum,
  SubscriptionPlansLayout,
} from '@components';
import {
  useAsyncInit,
  useNavOptionsSet,
  useSubscriptionPlansLayout,
} from '@hooks';
import {
  ProfileNavigatorNavigationPropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { IS_WEB, parseApiError } from '@utils';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { TariffPlanCodeEnum } from '@constants';
import { ToastService } from '@services';

const Subtitle = styled(BaseText)({
  textAlign: IS_WEB ? 'center' : 'left',
  variant: 'text.xlarge',
  color: '$heading800',
  fontWeight: 'semi',
});

const Description = styled(BaseText)({
  px: IS_WEB ? '$8' : '$0',
  textAlign: IS_WEB ? 'center' : 'left',
  variant: 'text.medium',
  color: '$text400',
});

const LoaderContainer = styled(View)({
  flex: [1, 0],
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: [null, 360],
});

const EmptyViewContainer = styled(LoaderContainer)({});

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Subscription,
  screens.ChangeSubscriptionPlan,
];

const MONTHLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Monthly,
};

const QUATERLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Quarterly,
};

const YEARLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Promote,
  code: TariffPlanCodeEnum.Yearly,
};

const SUBSCRIPTIONS = [MONTHLY_PLAN, QUATERLY_PLAN, YEARLY_PLAN];

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'ChangeSubscriptionPlan'>;

export const ChangeSubscriptionPlanScreen = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const nav = useNavigation<NavigationPropType>();
  const subscriptions = useSubscriptionPlansLayout(SUBSCRIPTIONS);

  useNavOptionsSet({ title: t('changeSubscriptionPlan.title') });

  useAsyncInit(async () => {
    try {
      await viewer.tariffPlans.fetchPlans.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  function navigateToPayment(tariffPlan: TariffPlanCodeEnum) {
    nav.navigate(screens.Payment, { tariffPlan });
  }

  const isError =
    Object.keys(viewer.tariffPlans.byCode).length === 0 &&
    viewer.tariffPlans.fetchPlans.isError;

  return (
    <SettingsLayout
      title={t('changeSubscriptionPlan.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <Subtitle>{t('changePlan.subtitle')}</Subtitle>
      <Separator size="$4" />
      <Description>{t('changePlan.description')}</Description>

      <Separator size="$7" />

      {viewer.tariffPlans.fetchPlans.inProgress && (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      )}

      {!viewer.tariffPlans.fetchPlans.inProgress && isError && (
        <EmptyViewContainer>
          <EmptyResultView />
        </EmptyViewContainer>
      )}

      {!viewer.tariffPlans.fetchPlans.inProgress && !isError && (
        <SubscriptionPlansLayout>
          {subscriptions.map((item, itemIndex) => (
            <SubscriptionPlanItem
              key={itemIndex}
              item={item}
              style={item.style}
              width={570}
              onPress={navigateToPayment}
              buttonText={t('chooseSubscriptionPlan.choosePlan')}
            />
          ))}
        </SubscriptionPlansLayout>
      )}
    </SettingsLayout>
  );
});
